import { ButtonSpinner } from "@/components/ButtonSpinner";
import { Loading } from "@/components/portal/Loading";
import { Button } from "@/components/ui/button";
import { FormEndingType } from "@/constants/form";
import { BASE_URL } from "@/dashboardQueries";
import { FormEnding } from "@/models/form";
import { useFormStore } from "@/store/form";
import { Menu } from "@headlessui/react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useFormik, useFormikContext } from "formik";
import {
  EllipsisVerticalIcon,
  ExternalLink,
  Monitor,
  PlusIcon,
  ScreenShare,
  TrashIcon,
  Wallpaper,
} from "lucide-react";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useEffect } from "react";

export const FormEndings = () => {
  const queryClient = useQueryClient();
  const { values } = useFormikContext();
  const { form, activeFormEnding, setActiveFormEnding, setActiveField } =
    useFormStore();

  // Fetch endings
  const {
    data: endings,
    isLoading,
    isError,
  } = useQuery(
    ["formEndings"],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/dashboard/form-endings?form=${form.id}`
      );
      return response.data;
    },
    {
      enabled: !!form?.id, // Only fetch if form.id exists
    }
  );

  // Create form ending mutation
  const createFormEnding = useMutation(
    async (formEnding: any) => {
      const response = await axios.post(
        `${BASE_URL}/dashboard/form-endings`,
        formEnding
      );
      return response.data;
    },
    {
      onSuccess: (newEnding) => {
        queryClient.invalidateQueries("formEndings"); // Invalidate the query to refresh the list
        setActiveFormEnding(newEnding); // Set the newly added form ending as active
        setActiveField(null); // Clear active field
      },
    }
  );

  // Delete form ending mutation
  const deleteFormEnding = useMutation(
    async (endingId: string) => {
      await axios.delete(`${BASE_URL}/dashboard/form-endings/${endingId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("formEndings"); // Invalidate the query to refresh the list
        if (endings?.length === 1) {
          setActiveFormEnding(null); // Clear active form ending
          setActiveField(form.fields[0]); // Switch back to field settings
        }
      },
    }
  );

  const handleCreateFormEnding = (type: string) => {
    createFormEnding.mutate({ form: form.id, endingType: type });
  };

  const handleDeleteFormEnding = (endingId: string) => {
    deleteFormEnding.mutate(endingId);
  };

  // Effect to handle mode change from quiz to general
  useEffect(() => {
    if (values.mode.id === "general" && endings?.length > 1) {
      const lastEnding = endings[endings.length - 1];
      handleDeleteFormEnding(lastEnding.id); // Remove the last form ending
    }
  }, [values.mode.id, endings]);

  const ENDING_TYPES = [
    {
      name: "End screen",
      icon: Wallpaper,
      type: "screen",
    },
    {
      name: "Redirect to a URL",
      icon: ScreenShare,
      type: "redirect",
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-8">
        <ButtonSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-red-500 flex items-center">
        <ExclamationCircleIcon className="h-5 w-5 mr-2" />
        <p>Failed to load endings.</p>
      </div>
    );
  }

  const handleEndingClick = (ending: FormEnding) => {
    setActiveFormEnding(ending);
    setActiveField(null);
  };

  const canAddMoreEndings =
    values.mode.id === "quiz" ||
    (values.mode.id === "general" && endings?.length === 0);

  return (
    <div className="relative">
      <Menu as="div">
        <div>
          <div
            className={`flex ml-1 items-center ${
              endings?.length > 0 ? "mt-0.5" : ""
            }`}
          >
            <h4 className="dark:text-zinc-400 text-zinc-500 font-medium text-sm">
              Endings
            </h4>
            {canAddMoreEndings && (
              <div className="flex flex-1 justify-end">
                <Menu.Button className="ml-2 rounded-md bg-white p-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 flex items-center">
                  <PlusIcon className="h-4 w-4" />
                </Menu.Button>
              </div>
            )}
          </div>
        </div>

        <Menu.Items className="px-1 translate-x-[100%] -top-1 absolute right-0 z-40 -mr-4 w-48 origin-top-left bg-white dark:bg-zinc-800 rounded-md py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in">
          <div className="text-xs ml-1.5 mt-2 dark:text-zinc-400 mb-2">
            Choose an ending type
          </div>
          {ENDING_TYPES.map((type) => (
            <Menu.Item key={type.name}>
              {({ active }) => (
                <button
                  onClick={() => handleCreateFormEnding(type.type)}
                  className={`${
                    active ? "bg-gray-100 dark:bg-zinc-700" : ""
                  } group flex w-full items-center rounded-md px-2 py-2 text-xs text-gray-900 dark:text-white`}
                >
                  <type.icon className="mr-2 h-4 w-4" />
                  {type.name}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Menu>

      {endings && (
        <div className={endings.length > 0 ? "mt-1 group" : "group"}>
          {endings?.map((ending: FormEnding) => (
            <div
              id={ending.id}
              className={`relative flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-primary pl-2 pr-3 py-2 rounded-md transition duration-200 ease-in-out ${
                activeFormEnding?.id === ending.id
                  ? "bg-gray-100 dark:bg-dark-primary hover:bg-dark-primary "
                  : ""
              }`}
              onClick={() => handleEndingClick(ending)}
            >
              <div
                className={`p-1 mr-2 items-center justify-center rounded-md dark:bg-stone-500 bg-stone-300`}
              >
                {ending.endingType == FormEndingType.screen ? (
                  <Wallpaper className="w-4 h-4 text-gray-900" />
                ) : (
                  <ScreenShare className="w-4 h-4 text-gray-900" />
                )}
              </div>
              <div className="flex-1 overflow-hidden flex relative">
                <h3 className="truncate text-ellipsis flex-1 text-sm text-gray-600 dark:text-gray-500 dark:text-white">
                  {ending.name ? (
                    ending.name
                  ) : (
                    <>
                      {ending.endingType === FormEndingType.screen ? (
                        <>End screen</>
                      ) : (
                        <>Redirect to a URL</>
                      )}
                    </>
                  )}
                </h3>
                {values?.mode.id === "quiz" && (
                  <span className="border dark:border-white/10 rounded-lg px-1.5 dark:bg-white/5 text-sm text-gray-500 dark:text-zinc-400">
                    {ending.minScore != null ? ending.minScore : "??"} to{" "}
                    {ending.maxScore != null ? ending.maxScore : "??"}
                  </span>
                )}
              </div>
              <Menu
                as="div"
                className="z-20 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <div>
                  <Menu.Button className="-mr-1 flex items-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none">
                    <span className="sr-only">Open options</span>
                    <EllipsisVerticalIcon
                      aria-hidden="true"
                      className="size-5"
                    />
                  </Menu.Button>
                </div>

                <Menu.Items className="translate-x-[100%] top-0 absolute right-0 z-40 -mr-4 -mt-0.5 w-48 origin-top-left bg-white dark:bg-zinc-800 rounded-md py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in">
                  <Menu.Item>
                    <button
                      onClick={() => handleDeleteFormEnding(ending.id)}
                      type="button"
                      className="flex items-center block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none dark:text-white"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete
                    </button>
                  </Menu.Item>
                </Menu.Items>
              </Menu>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
